// Galaxy Galleria immersive runtime — standalone Vite entry.
//
// Bootstrap order:
//   1. Initialise Firebase Auth + listen for state changes.
//   2. If signed in: load boot config (scene + gateway) and bootstrap the
//      immersive runtime.
//   3. If signed out: render the auth gate (sign-in / sign-up form, with
//      a thank-you splash if the user just signed out).
//   4. On auth-state transition: tear down the current surface and mount
//      the other. Page never reloads.
//
// Bootstrap config sources are unchanged from before auth landed:
//   - boot.json (fetched from the bundle root) → scene data
//   - VITE_GATEWAY_URL / VITE_GATEWAY_SHOP_ID (build-time env) → gateway
// The Firebase ID token (when present) is passed through to GcgCartTransport
// via the boot config; the transport attaches it to `POST /shops/.../sessions`
// once GCG iter-5a lands. Anonymous sessions continue to work meanwhile.

import "@css/kapowx-runtime-test.css";
import { bootstrapRuntimeTestApp } from "@runtime/app.js";
import { installZoomGuard } from "@runtime/_zoom-guard.js";   // ASK-139 §94.10 ruling 3 — the page never zooms (before the gate draws)
import { resolveAuthority, describeAuthority } from "@runtime/_authority.js";   // ASK-104 ship 4 — the one authority read
import { readBootSchema, describeBootSchema } from "@runtime/_boot-schema.js";   // ASK-131 §2b — the boot schema window, permissive-first (absent = legacy)
import { sceneBootUrl } from "@runtime/_asset-url-resolver.js";   // ASK-177 preview path — the boot object (VITE_SCENE_BOOT_OBJECT, default boot.json)
import { earlyJson, sessionHint, urlNamesHub } from "@runtime/_early-fetch.js";   // ASK-178 §3 item 2 (D-088 Slice 0): the fetches index.html started at parse
import { initFirebase, subscribeAuth, subscribeIdToken, getIdToken, getIdTokenClaims, signOut, consumeGoogleRedirect } from "./firebase";
import { AuthGate } from "./auth-gate";
import { guestEntryAllowed, guestLinkToken, stashGuestToken, guestsMayFollow } from "./guest-entry";   // ASK-172 Client 5b item 1 — the anonymous dev entry (meet link, door-gated)
import { isLocked, renderHoldingPage } from "./access-mode";
import type { User } from "firebase/auth";

type BootConfig = {
	title?: string;
	_bootUrl?: string;   // M7 Chunk 3 — source URL the live-refresh poller re-fetches (ASK-080 §E)
	[key: string]: unknown;   // boot.json carries shops/projectAssets/librarySha/etc. consumed by app.js
	gatewayConfig?: {
		baseUrl:      string;
		// Default shopId used for the cart/session transport. The product
		// feed is now fetched per-shop on portal entry (see
		// `_fetchProductsForShop` in app.js), so this is no longer the
		// "the one shop" identifier — just the initial cart/session shop.
		shopId:       string;
		apiNamespace: string;
		// firebaseIdToken set by main.ts when a customer is signed in.
		// GcgCartTransport reads it and attaches `Authorization: Bearer ...`
		// on session creation. Null/undefined = anonymous session.
		firebaseIdToken?: string | null;
		// ASK-172 Client 5b — the IDENTITY token for the social doors (invites, chat, handle, blocks), separate from the
		// cart/session token above: a GUEST (anonymous uid, dev) carries one here, while its cart session stays anonymous
		// (the sessions door refuses an anonymous Firebase token: 401 token_has_no_email, measured 2026-10-27).
		getIdentityToken?: () => Promise<string | null>;
		// E3 (ASK-100) — live token provider. `firebaseIdToken` above is a snapshot from page
		// mount; Firebase ID tokens expire after 1h, so any Studio session older than that saw
		// every admin write 401. Transports call this per request: the SDK returns its cached
		// token while valid and transparently refreshes when it isn't.
		getFreshIdToken?: () => Promise<string | null>;
	} | null;
	// Canonical multi-shop surface — keyed by projectId. The runtime reads
	// this directly; adding a new shop is a project.json + GLB drop with
	// no code edits required. See PLATFORM_VISION §2.7.
	shops?: Record<string, unknown>;
	// Shop-template preset library, keyed by templateId then category.
	// Inlined into the minted boot.json by Forge (and into the
	// wp_localize_script payload by the PHP repository) so the runtime
	// composition step can resolve any knob without an extra fetch.
	templatePresets?: Record<string, unknown>;
	// Legacy top-level keys — retained for 24h backward-compat window after
	// the shops-map refactor lands. Cached old bundles read these; new
	// bundles use `shops` directly. Drop the *Bootstrap fields once the
	// migration window closes.
	hubBootstrap?: unknown;
	nocturnBootstrap?: unknown;
	monsterSquadBootstrap?: unknown;
	projectAssets?: Record<string, unknown>;
	// Customer-auth hooks passed in by main.ts. The runtime uses
	// onSignOut to wire the settings-panel sign-out button; customerEmail
	// shows in the same panel for orientation.
	customerEmail?: string | null;
	onSignOut?: () => Promise<void>;
	// Resolved authority role — surfaces the signed-in user's authority to the
	// runtime so it can lazy-load the Shop Design Studio overlay when the user
	// is a merchant (or admin, for testing). null when signed-out or when the
	// identity holds no authority bundle. Values: "customer" | "merchant" | "admin".
	//
	// ASK-104 ship 4: no longer the flat `claims.role` — it is derived from the
	// resolved authority below (`/admin/me` when present, claim union otherwise),
	// so a claim-shape change can't silently strand a merchant on a customer view.
	userRole?: string | null;
	// ASK-103 slice E — the console origin the Studio's "↩ Console" door points at. Build-time env
	// (`VITE_CONSOLE_URL`) so dev and prod each point at their own console; the doors module carries
	// the deployed default, so an unset env still opens a working door rather than none.
	// It is an ORIGIN, never a credential — S4: the door carries context, the console reads its own claim.
	consoleUrl?: string;
	// The full resolved authority (bundles + shops + grantsEpoch + source).
	// `@runtime/_authority.js` is the ONLY module that produces this; nothing
	// downstream may parse a claim (permission-model-spec §5, ASK-104).
	authority?: Record<string, unknown> | null;
	// Resolves with `authority` once the read lands (it also sets `authority` + `userRole`); every reader awaits it first.
	authorityReady?: Promise<Record<string, unknown>>;
	// ASK-169 (Client 4w) — presence: the signed-in uid and a LAZY opener for the room transport (Firebase RTDB). The app
	// reads the plane's `presence{enabled, databaseURL}` off the public door and calls `openTransport` only when enabled,
	// so `firebase/database` is never downloaded on a plane with presence off.
	presence?: {
		uid: string;
		anonymous?: boolean;
		openTransport: (cfg: { databaseURL: string; emulator?: string | null }) => Promise<unknown>;
	};
};

async function loadBootConfig(idToken: string | null, { guest = false }: { guest?: boolean } = {}): Promise<BootConfig> {
	let cfg: BootConfig = {};

	// Scene data source — the Forge-published Scene CDN, and nothing else.
	//
	// DECISION-014 (2026-05-23) made this runtime a pure static SPA that reads Forge-published
	// artifacts. A second path survived anyway: with `VITE_SCENE_CDN_URL` unset this fetched
	// `${BASE_URL}boot.json`, the bundled boot that `build-boot-json.mjs` generated. ASK-124 B2 /
	// RULING 32 finished that deletion — the generator is gone, so the bundled file no longer
	// exists and fetching it could only ever 404 into the SPA's index.html.
	//
	// 📌 ABSENCE IS NEVER A DEFAULT. `vite.config.ts` refuses to BUILD without a CDN base, so this
	// branch cannot be reached in a real bundle; the throw is the belt to that braces. Degrading
	// silently is what let a misconfigured build ship reading a stale frozen boot for months.
	const sceneCdnBase: string | undefined = import.meta.env.VITE_SCENE_CDN_URL;
	if (!sceneCdnBase) throw new Error("[runtime] VITE_SCENE_CDN_URL is unset — there is no bundled boot to fall back to (ASK-124 B2 / RULING 32). This bundle was built wrong.");
	// ASK-177 preview path: the boot OBJECT is `boot.json` unless a preview build names a candidate boot (sceneBootUrl).
	const bootUrl = sceneBootUrl() ?? `${sceneCdnBase.replace(/\/$/, "")}/boot.json`;
	// 🔴 ASK-131 D6 — A BOOT WE CANNOT FETCH IS NOT AN EMPTY WORLD, AND IT IS NOT SILENT.
	//
	// This used to be: `if (r.ok) cfg = await r.json()`, with a bare `catch {}` and a comment. On a
	// non-ok response the config stayed `{}`; on a network error the throw was swallowed. Nothing
	// logged, nothing retried. The customer got the "No scene available. Publish a scene from the
	// authoring tool first." empty state — a sentence that is FALSE and actionable in the wrong
	// direction — and we got no signal at all. A CDN blip during a tester's walk reads as a confusing
	// FAIL against working product, which is exactly what this ASK is protecting the ladder from.
	//
	// Three tries with backoff and jitter: a blip is the common case and one extra second beats an
	// empty world. Then a REAL failure state, carried on the config so the app can tell "the boot did
	// not arrive" from "the boot arrived and has no shops" — absent and empty, again, and the two
	// need different sentences because they need different actions.
	const BOOT_TRIES = 3;
	let lastFailure: { status: number | null; message: string } = { status: null, message: "not attempted" };
	for (let attempt = 1; attempt <= BOOT_TRIES; attempt++) {
		try {
			// attempt 1 takes the fetch index.html started at parse (same URL, cache-busted, no-store) — it is no longer behind
			// Firebase auth's token refresh; an early fetch that failed is simply not used (this attempt fetches as before)
			const early = attempt === 1 ? await (earlyJson("boot", bootUrl, { take: true }) as Promise<{ ok: boolean; status: number | null; json?: unknown } | null> | null) : null;
			const r = early?.ok ? { ok: true, status: early.status ?? 200, json: async () => early.json } : await fetch(bootUrl, { cache: "no-store" });
			if (r.ok) {
				const payload = await r.json();
				// ASK-131 §2b — the shape check belongs at EVERY fetch site, and this is the customer's
				// own. A boot we cannot read is a failure to be retried and reported, not a config to
				// render from: rendering it half-understood is how a breaking change reaches a customer
				// as an empty world instead of as an error. Permissive-first — an UNSTAMPED boot is
				// accepted as legacy (§7b), which is what today's live boot is.
				const schema = readBootSchema(payload);
				if (!schema.ok) {
					lastFailure = { status: r.status, message: describeBootSchema(schema) };
				} else {
					cfg = payload;
					console.log(`[runtime] scene config from ${bootUrl} (Forge CDN) — ${schema.detail}${attempt > 1 ? `, after ${attempt} attempts` : ""}`);
					lastFailure = { status: null, message: "" };
					break;
				}
			} else {
				lastFailure = { status: r.status, message: `HTTP ${r.status}` };
			}
		} catch (err) {
			lastFailure = { status: null, message: (err as Error)?.message || String(err) };
		}
		console.warn(`[runtime] boot fetch attempt ${attempt}/${BOOT_TRIES} failed (${lastFailure.message}) — ${bootUrl}`);
		if (attempt < BOOT_TRIES) {
			// 300ms, 900ms, with jitter so a fleet of clients does not retry in lockstep.
			const backoffMs = 300 * Math.pow(3, attempt - 1) * (0.75 + Math.random() * 0.5);
			await new Promise((res) => setTimeout(res, backoffMs));
		}
	}
	if (lastFailure.message) {
		// Named on the config, not thrown: the gateway env below may still supply enough to run, and
		// a shop that can run should run. The app decides what to show; this records what happened.
		(cfg as Record<string, unknown>)._bootFetchError = {
			url: bootUrl, attempts: BOOT_TRIES, status: lastFailure.status, message: lastFailure.message,
		};
		console.error(`[runtime] boot.json unreachable after ${BOOT_TRIES} attempts (${lastFailure.message}) — ${bootUrl}. The runtime will show a load-failure state, not an empty shop.`);
	}

	// Gateway config — build-time env wins. Layers on top of (or fills in for)
	// boot.json. Carries the Firebase ID token through to the cart transport.
	const gatewayUrl: string | undefined = import.meta.env.VITE_GATEWAY_URL;
	const shopId:     string             = import.meta.env.VITE_GATEWAY_SHOP_ID || "demo-store";

	if (gatewayUrl) {
		const trimmed = gatewayUrl.replace(/\/$/, "");
		cfg.gatewayConfig = {
			baseUrl:         trimmed,
			shopId,
			apiNamespace:    "kapowx/v1",
			firebaseIdToken: guest ? null : idToken,
			getFreshIdToken: guest ? async () => null : () => getIdToken(),   // E3 — per-request freshness (SDK-cached, auto-refreshing); a guest's cart is anonymous
			getIdentityToken: () => getIdToken(),
		};
		console.log(`[runtime] gateway from build env — ${trimmed} (default shop=${shopId}, auth=${idToken ? "yes" : "anonymous"})`);
	} else if (!cfg.gatewayConfig) {
		console.warn("[runtime] no gateway configured — shops will load with empty catalogs");
	}

	// ASK-103 slice E — console origin for the door out. Env wins; unset falls back to the doors
	// module's deployed default at the point of use.
	const consoleUrl: string | undefined = import.meta.env.VITE_CONSOLE_URL;
	if (consoleUrl) cfg.consoleUrl = consoleUrl.replace(/\/$/, "");

	if (!cfg.title) cfg.title = "Galaxy Galleria";
	cfg._bootUrl = bootUrl;   // M7 Chunk 3 — live-refresh poller re-fetches the exact source (ASK-080 §E)
	return cfg;
}

let currentSurface: "auth" | "runtime" | "pending" | "network" | null = null;
// ASK-172 Client 5e item 0b — the identity this page's runtime (and its presence room) was mounted for, and the room's
// transport: when the PROFILE's account changes under the page (another tab signs in or out — one browser profile holds ONE
// Firebase user), the room's socket is dropped at once, before the database re-auths as the new account, so the server's
// onDisconnect removal runs as the node's owner; then the page follows the profile's account (a reload re-judges it).
let runtimeUid: string | null = null;
let presenceTransport: { close(): void } | null = null;
let authGate: AuthGate | null = null;

async function mountRuntime(root: HTMLElement, user: User): Promise<void> {
	if (currentSurface === "runtime") return;
	currentSurface = "runtime";
	runtimeUid = user.uid;
	sessionHint(true);   // D-088 Slice 0: the next load of this profile may fetch the street ahead of auth (cleared at sign-out)
	if (authGate) { authGate.destroy(); authGate = null; }
	// ASK-139 F-139c — the 4-way standard (never nothing on screen > 300 ms). This used to clear the
	// page and then await the token, the boot and the authority round-trip — measured 1.5 s of black on
	// an emulated phone. The boot card stays up, saying what is happening, until the runtime replaces it.
	root.innerHTML = `
		<div class="kapowx-authoring-page__boot" data-runtime-preboot role="status" aria-live="polite">
			<p class="kapowx-authoring-page__boot-eyebrow">Galaxy Galleria</p>
			<h1>Immersive Storefronts</h1>
			<p>Opening the shop…</p>
		</div>`;
	const idToken = await getIdToken();
	const guest   = user.isAnonymous === true;   // only ever mounted for a guest the door allowed (main below)
	const config  = await loadBootConfig(idToken, { guest });
	const claims  = await getIdTokenClaims();
	// ASK-104 ship 4 — authority comes from the authorization plane when it answers
	// (`GET /admin/me`, ship 1) and from the claim UNION otherwise; never from flat
	// `claims.role` alone. Bounded + fail-soft, so a slow/absent plane degrades to the
	// claim instead of stalling the boot or stranding a merchant on a customer view.
	//
	// D-088 cl.1 Slice 0 (ASK-178 §3 item 2): the read is STARTED here and AWAITED where authority is read — the Studio gate
	// (`_loadStudioInner`), a shop's first compose (its feed's `live` flag), the operator chip — never in front of the app's
	// construction. Measured on the hub (12:31Z): 0.65–0.93 s of `/admin/me` (a CORS preflight + the GET) sat in series between
	// the boot and the street's first request, and the street reads no authority to draw.
	config.customerEmail = user.email;
	config.authorityReady = resolveAuthority({ gatewayConfig: config.gatewayConfig, claims }).then((authority) => {
		config.authority = authority;
		config.userRole  = authority.role;
		console.log(`[auth] authority — ${describeAuthority(authority)}`);
		return authority;
	});
	config.presence      = {
		uid: user.uid,
		anonymous: user.isAnonymous,   // ASK-172 §5.3: a guest may follow a link (dev) but never mint one
		openTransport: async (cfg) => {
			const t = await (await import("./presence-transport")).openPresenceTransport({ app: initFirebase().app, uid: user.uid, databaseURL: cfg.databaseURL, emulator: cfg.emulator ?? null });
			presenceTransport = t;   // item 0b: the identity watcher below can drop its socket
			return t;
		},
	};
	config.onSignOut     = async () => {
		sessionHint(false);
		stashGuestToken(null);   // item 0: a guest who signs out leaves the link behind (the party's own key keeps the link for a sign-in)
		AuthGate.markSignedOut();
		await signOut();
		// subscribeAuth listener below catches the transition and reloads.
	};
	// Debug helper for verifying custom-claim provisioning (role-scoping
	// rollout). Force-refreshes the token so newly-granted claims show up
	// without waiting for the SDK's cache TTL. Usage: `await __kapowxClaims()`.
	(window as unknown as { __kapowxClaims: () => Promise<Record<string, unknown> | null> }).__kapowxClaims =
		() => getIdTokenClaims(true);
	// ASK-104 ship 4 — the same helper one level up: what the runtime actually RESOLVED
	// (plane vs claim), which is the question to ask when a Studio doesn't appear.
	(window as unknown as { __kapowxAuthority: () => Promise<Record<string, unknown>> }).__kapowxAuthority =
		async () => resolveAuthority({ gatewayConfig: config.gatewayConfig, claims: await getIdTokenClaims(true) });
	bootstrapRuntimeTestApp(root, config);
}

function mountAuthGate(root: HTMLElement): void {
	if (currentSurface === "auth") return;
	currentSurface = "auth";
	if (authGate) { authGate.destroy(); authGate = null; }
	// The runtime mounts deep DOM + Three.js — easiest reset is replace the
	// whole root content. AuthGate.destroy() clears its own listeners. The
	// runtime app holds resources we can't cleanly tear down without a page
	// reload, so on subsequent sign-ins we reload the page (see below).
	root.innerHTML = "";
	authGate = new AuthGate(root);
}

// "Pending approval" surface — shown when a signed-in user doesn't yet
// have the `approved: true` custom claim. AuthGate polls the token every
// 30s; when the claim flips, onApproved fires and we swap to the runtime
// without a page reload (cleaner UX than refresh-and-pray).
function mountPending(root: HTMLElement, user: User): void {
	if (currentSurface === "pending") return;
	currentSurface = "pending";
	if (authGate) { authGate.destroy(); authGate = null; }
	root.innerHTML = "";
	authGate = new AuthGate(root, {
		pendingEmail: user.email,
		onApproved:   () => { void mountRuntime(root, user); },
	});
}

// ASK-159 §2.17 item 8 (card 123qy9rtt0a) — a cached session whose token cannot be refreshed (the sign-in hosts do not
// resolve: `auth/network-request-failed`). Was an unhandled rejection in the auth listener: a console error and nothing
// on screen, no way to change user. Now: the words, a Retry (the same check again), and a sign-out that needs no network.
function mountNetworkFailure(root: HTMLElement, user: User, err: unknown): void {
	const code = (err as { code?: string })?.code || "unknown";
	console.warn(`[auth] session check failed — ${code}`);
	currentSurface = "network";
	if (authGate) { authGate.destroy(); authGate = null; }
	root.innerHTML = "";
	authGate = new AuthGate(root, { network: { code, email: user.email, tries: 0, onRetry: async () => {
		const claims = await getIdTokenClaims(true);   // throws again while the hosts are unreachable
		if (claims?.approved === true) void mountRuntime(root, user); else mountPending(root, user);
	} } });
}

async function main(): Promise<void> {
	installZoomGuard(document);
	// ASK-066 lockdown — prod ships locked (holding page, nothing mounts).
	// Reopening = flip VITE_ACCESS_MODE in the env + redeploy.
	if (isLocked()) { renderHoldingPage(); return; }

	const root = document.querySelector<HTMLElement>("[data-kapowx-runtime-test]");
	if (!root) {
		console.error("[runtime] Mount point [data-kapowx-runtime-test] not found");
		return;
	}

	initFirebase();
	// Complete a redirect-mode Google sign-in if we're landing from one
	// (popup-blocked environments). No-op otherwise; subscribeAuth picks up
	// the resulting user exactly like a popup sign-in.
	void consumeGoogleRedirect();

	// First auth-state event fires synchronously with cached state (or null).
	// Subsequent events fire on every transition. After each event we also
	// check the `approved` custom claim — un-approved signed-in users go
	// to the pending-approval surface rather than the runtime.
	let firstEvent = true;
	let guestUid: string | null = null;   // item 0: the guest uid this page mounted the runtime for
	// item 0b: registered BEFORE any room opens, so it runs ahead of the database's own token listener
	subscribeIdToken((u) => {
		if (!runtimeUid || (u?.uid ?? null) === runtimeUid) return;
		console.log(`[auth] this profile's account changed under the page (${u ? "another account" : "signed out"}) — leaving the room as ${runtimeUid.slice(0, 6)}…`);
		try { presenceTransport?.close(); } catch { /* already offline */ }
		presenceTransport = null;
	});
	subscribeAuth(async (user) => {
		const wasRuntime = currentSurface === "runtime";
		// item 0b: the runtime was mounted for another identity (another tab of this profile signed in as someone else, or as a
		// fresh guest) — follow the profile's account with a clean boot; the room already let the old identity go (above)
		if (wasRuntime && runtimeUid && user && user.uid !== runtimeUid) {
			console.log("[auth] reloading as the profile's current account");
			window.location.reload();
			return;
		}

		if (!user) {
			if (firstEvent) { firstEvent = false; mountAuthGate(root); return; }
			if (currentSurface === "network") { mountAuthGate(root); return; }   // "use another account": straight to the form
			if (wasRuntime || currentSurface === "pending") {
				AuthGate.markSignedOut();
				window.location.reload();
			}
			return;
		}

		// ASK-172 Client 5b item 1 — a GUEST (anonymous uid): the runtime mounts only while the plane's public door says
		// presence.anonymousMayFollow AND there is a party link to follow; otherwise the anonymous session is ended and the
		// gate shows. No approval claim is asked of a guest (it reads only — every write door refuses an anonymous uid).
		if (user.isAnonymous) {
			// item 0 (ALPHA012 step 4): a guest runtime already mounted for THIS uid is never re-judged by a later auth event —
			// the entry was decided once, at the mount (a re-judge read the stripped URL and signed the guest out to the splash)
			if (currentSurface === "runtime" && guestUid === user.uid) { firstEvent = false; return; }
			const allowed = await guestEntryAllowed();
			const first = firstEvent;
			firstEvent = false;
			if (!allowed) {
				// item 0: sign a guest session OUT only where the door closes guests (prod; a stale anonymous session). Where guests
				// may follow, a tab with no link of its own just shows the gate — the profile's anonymous session belongs to the tab
				// that holds the link (signing out here signed THAT tab out to the splash: another tab of one profile shares it)
				// item 0b: and the refused anonymous user is DELETED (it never follows anything again; signing it out only orphaned it)
				if (!(await guestsMayFollow())) { stashGuestToken(null); await user.delete().catch(() => signOut().catch(() => {})); }
				if (currentSurface !== "auth") mountAuthGate(root);
				return;
			}
			if (first) { guestUid = user.uid; stashGuestToken(await guestLinkToken()); void mountRuntime(root, user); return; }
			if (currentSurface === "auth") window.location.reload();   // the gate's "Continue as a guest" → a clean Three.js boot
			return;
		}

		// Signed in — check whether they've been approved by the admin.
		// Force-refresh on first event so a freshly-approved user doesn't
		// need to wait for the SDK's cache TTL to see the new claim.
		let claims: Record<string, unknown> | null;
		try { claims = await getIdTokenClaims(firstEvent); }
		catch (err) { firstEvent = false; mountNetworkFailure(root, user, err); return; }
		const approved = claims?.approved === true;

		if (firstEvent) {
			firstEvent = false;
			if (approved) void mountRuntime(root, user);
			else          mountPending(root, user);
			return;
		}

		// Transitions after first paint:
		//  - auth gate → signed-in: reload page for clean Three.js boot
		//    (only if approved; un-approved goes to pending, no reload).
		//  - any → signed-out: handled in the !user branch above.
		if (currentSurface === "auth") {
			if (approved) window.location.reload();
			else          mountPending(root, user);
		}
	});
}

// ── D-088 cl.1 Slice 0 (ASK-178 §3 item 2): THE STREET'S BYTES START WHILE AUTH RESOLVES. On a hub URL, for a profile that
// mounted the runtime before (the session hint — a signed-out visitor never downloads a street it cannot enter), the street's
// GLBs + sky pair are fetched as soon as the early boot + registry land (`_hub-critical-path.js#prefetchHubBytes`, a lazy
// chunk), in parallel with Firebase's token refresh and the authority read instead of after them. Everything that consumes
// them falls back to its own fetch.
try {
	const cdn = (import.meta.env.VITE_SCENE_CDN_URL as string | undefined)?.replace(/\/$/, "");
	const bootUrl = sceneBootUrl() ?? (cdn ? `${cdn}/boot.json` : null);
	const boot = bootUrl && urlNamesHub(window.location.search) && sessionHint() ? earlyJson("boot", bootUrl) : null;
	if (boot) void import("@runtime/_hub-critical-path.js").then((m) => m.prefetchHubBytes(boot, earlyJson("doorSlots"))).catch(() => {});
} catch { /* the head start is a nicety: every consumer fetches on its own */ }

main().catch((err) => {
	console.error("[runtime] Boot failed:", err);
});
